import {
  ApiAlertConditionGroup,
  ApiConditionValueType,
  ApiOperator,
} from "@incendium/api";
import {
  Box,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  TextField,
} from "@mui/material";
import ConditionalGroup from "Components/ConditionalGroup/ConditionalGroup";
import { useConditionGroupContext } from "Components/ConditionalGroup/ConditionGroupProvider";
import InputGroup, { InputGroupItem } from "Components/InputGroup/InputGroup";
import { MetricSelector } from "features/analytics";
import produce from "immer";
import { useEffect, useMemo } from "react";

type TConditionOption = {
  operator: ApiOperator;
  valueType: ApiConditionValueType;
  label: string;
};

const valueOperators: [ApiOperator, string][] = [
  [ApiOperator.IS_GREATER_THAN, "Is greater than"],
  [ApiOperator.IS_GREATER_THAN_OR_EQUAL, "Is greater than or equal to"],
  [ApiOperator.IS_LESS_THAN, "Is less than"],
  [ApiOperator.IS_LESS_THAN_OR_EQUAL, "Is less than or equal to"],
  [ApiOperator.EQUAL, "Is equal to"],
];

const changeOperators: [ApiOperator, string][] = [
  [ApiOperator.IS_GREATER_THAN, "Increased by more than"],
  [ApiOperator.IS_GREATER_THAN_OR_EQUAL, "Increased by at least"],
  [ApiOperator.IS_LESS_THAN, "Decreased by more than"],
  [ApiOperator.IS_LESS_THAN_OR_EQUAL, "Decreased by at least"],
  [ApiOperator.EQUAL, "Changed by exactly"],
];

const changeUnits: [ApiConditionValueType, string][] = [
  [ApiConditionValueType.PERCENTAGE_DIFFRENCE, "%"],
  [ApiConditionValueType.NUMBER_DIFFRENCE, "number"],
];

const conditionOptionGroups: { label: string; options: TConditionOption[] }[] =
  [
    {
      label: "Value",
      options: valueOperators.map(([operator, label]) => ({
        operator,
        valueType: ApiConditionValueType.UNSPECIFIED,
        label,
      })),
    },
    {
      label: "Change vs previous period",
      options: changeOperators.flatMap(([operator, label]) =>
        changeUnits.map(([valueType, unit]) => ({
          operator,
          valueType,
          label: `${label} (${unit})`,
        })),
      ),
    },
  ];

const conditionOptions = conditionOptionGroups.flatMap((g) => g.options);

const toConditionKey = (
  operator: ApiOperator,
  valueType: ApiConditionValueType,
) => `${operator}|${valueType}`;

const Row = ({ groupIdx, rowIdx }: { groupIdx: number; rowIdx: number }) => {
  const { conditionGroups, setConditionGroups } =
    useConditionGroupContext<ApiAlertConditionGroup>();
  const condition = useMemo(() => {
    return (conditionGroups[groupIdx]?.conditions || [])[rowIdx];
  }, [conditionGroups, groupIdx, rowIdx]);

  const selectedOption = conditionOptions.find(
    (o) =>
      o.operator === condition?.operator &&
      o.valueType ===
        (condition?.valueType || ApiConditionValueType.UNSPECIFIED),
  );

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <InputGroup>
            <InputGroupItem>
              <Box
                sx={{
                  ".metric-selector-static-attribution-controls": { ml: 2 },
                }}
              >
                <MetricSelector
                  metric={condition?.metric || null}
                  size="medium"
                  hasControls={false}
                  overrideIsStatic
                  onChange={(e, v) => {
                    setConditionGroups(
                      produce(
                        conditionGroups,
                        (draft: ApiAlertConditionGroup[]) => {
                          (draft[groupIdx]?.conditions || [])[rowIdx].metric =
                            v.metric;
                        },
                      ),
                    );
                  }}
                />
              </Box>
            </InputGroupItem>
            <InputGroupItem>
              <TextField
                select
                fullWidth
                size="medium"
                label="Condition"
                value={
                  selectedOption
                    ? toConditionKey(
                        selectedOption.operator,
                        selectedOption.valueType,
                      )
                    : ""
                }
                onChange={(e) => {
                  const option = conditionOptions.find(
                    (o) =>
                      toConditionKey(o.operator, o.valueType) ===
                      e.target.value,
                  );
                  if (!option) return;
                  setConditionGroups(
                    produce(
                      conditionGroups,
                      (draft: ApiAlertConditionGroup[]) => {
                        const draftCondition = (draft[groupIdx]?.conditions ||
                          [])[rowIdx];
                        draftCondition.operator = option.operator;
                        draftCondition.valueType = option.valueType;
                      },
                    ),
                  );
                }}
              >
                {conditionOptionGroups.flatMap((group) => [
                  <ListSubheader key={group.label}>
                    {group.label}
                  </ListSubheader>,
                  ...group.options.map((option) => {
                    const key = toConditionKey(
                      option.operator,
                      option.valueType,
                    );
                    return (
                      <MenuItem key={key} value={key}>
                        {option.label}
                      </MenuItem>
                    );
                  }),
                ])}
              </TextField>
            </InputGroupItem>
            <InputGroupItem>
              <TextField
                fullWidth
                size="medium"
                label="Value"
                type="number"
                value={condition?.value || ""}
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment:
                    selectedOption?.valueType ===
                    ApiConditionValueType.PERCENTAGE_DIFFRENCE ? (
                      <InputAdornment position="end">%</InputAdornment>
                    ) : undefined,
                }}
                onChange={(e) => {
                  setConditionGroups(
                    produce(
                      conditionGroups,
                      (draft: ApiAlertConditionGroup[]) => {
                        (draft[groupIdx]?.conditions || [])[rowIdx].value =
                          e.target.value;
                      },
                    ),
                  );
                }}
              />
            </InputGroupItem>
          </InputGroup>
        </Grid>
      </Grid>
    </>
  );
};

function AlertRules() {
  const { setRowComponent } = useConditionGroupContext();

  useEffect(() => {
    setRowComponent(() => (groupIdx, rowIdx) => {
      return <Row groupIdx={groupIdx} rowIdx={rowIdx} />;
    });
  }, [setRowComponent]);

  return (
    <Box>
      <ConditionalGroup />
    </Box>
  );
}

export default AlertRules;
